'use client';

import { twMerge } from 'tailwind-merge';

import { RichText } from '@lamesarv-sdk/components';

import { BaseField, BaseMessages, BaseSubmit, BaseSubscribeField, BaseTermsField } from './Base';

import { IEventRegistrationFormProps } from '../types';
import { Fragment } from 'react';

// --

const styles = {
  container: 'leading-none grow relative',
  label:
    'absolute left-3 top-5 pointer-events-none leading-none text-black-400 text-sm md:text-md text-left [&>span]:text-md [&>span]:leading-none transition-all group-focus-within:left-2 group-focus-within:top-2 group-focus-within:-translate-y-0 group-focus-within:text-xs transition-all',
  labelWithValue: 'left-2 top-2 -translate-y-0 text-xs',
  input:
    'leading-none px-3 py-5 rounded-md focus:outline-none focus:ring-0 focus:border-black-500 focus:px-3 focus:pt-8 focus:pb-2',
  inputWithValue: 'px-3 pt-8 pb-2',
};

// --

interface IStyleSimpleFormProps extends IEventRegistrationFormProps {
  wasSuccessful: boolean;
  wasError: boolean;
}

export const GeneralForm = (props: IStyleSimpleFormProps) => {
  const attendanceOptions =
    props.attendance_general?.map((option) => ({
      label: option.text,
      value: option.text,
    })) || [];

  const interestsOptions =
    props.interests_general?.map((option) => ({
      label: option.text,
      value: option.text,
    })) || [];

  return (
    <Fragment>
      {props.extract_header && (
        <div className="w-full md:w-[720px] xl:w-[865px] mx-auto flex flex-col gap-4 p-1 md:p-8 text-center justify-center">
          {props.title && <div className={twMerge('heading-h4 leading-none', props.classes_title)}>{props.title}</div>}
          <div className="text-md-medium leading-none">All fields marked with an asterisk (*) are required.</div>
          {props.description && (
            <div className={twMerge('text-md-regular', props.classes_description)}>
              <RichText content={props.description} />
            </div>
          )}
        </div>
      )}
      <div
        className={twMerge(
          'w-full md:w-[720px] xl:w-[865px] mx-auto flex flex-col gap-4 p-1 md:p-8 text-center justify-center bg-white md:border border-black-300 rounded-lg',
          props.classes,
        )}
      >
        {!props.extract_header && (
          <Fragment>
            {props.title && (
              <div className={twMerge('heading-h4 leading-none', props.classes_title)}>{props.title}</div>
            )}
            <div className="text-md-medium leading-none">All fields marked with an asterisk (*) are required.</div>
            {props.description && (
              <div className={twMerge('text-md-regular', props.classes_description)}>
                <RichText content={props.description} />
              </div>
            )}
          </Fragment>
        )}
        <div className="relative w-full mx-auto mt-4 grid grid-cols-2 gap-4 md:gap-6 items-start">
          <BaseField type="first-name" required={true} styleClasses={styles} />
          <BaseField type="last-name" required={true} styleClasses={styles} />
          <BaseField type="email" required={true} styleClasses={styles} />
          <BaseField type="phone" required={true} styleClasses={styles} />
          {attendanceOptions.length > 0 && (
            <BaseField
              type="attendance"
              optionsList={attendanceOptions}
              required={true}
              classes_container_label="text-md-bold"
              classes_label="text-md-regular"
              extraClasses="col-span-2"
            />
          )}
          {interestsOptions.length > 0 && (
            <BaseField
              type="interests"
              optionsList={interestsOptions}
              required={false}
              classes_container_label="text-md-bold"
              classes_label="text-md-regular"
              extraClasses="col-span-2"
            />
          )}
          <BaseField type="comments" required={false} styleClasses={styles} />
        </div>
        <div className="relative flex flex-col gap-4 w-full mx-auto mt-4">
          {props.display_subscribe_checkbox && <BaseSubscribeField {...props} />}
          {props.display_terms_checkbox && <BaseTermsField {...props} />}
        </div>
        <div className="relative w-full mx-auto">
          <BaseMessages {...props} />
        </div>
        {!props.wasSuccessful && (
          <div className="relative w-full mx-auto">
            <BaseSubmit
              {...props}
              styleClasses="bg-blue-500 text-white rounded-sm text-[16px] py-3 px-3"
              extraClasses="w-full"
            />
          </div>
        )}
      </div>
    </Fragment>
  );
};
